import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { LexicalMarkdownEditor } from "@/components/ui/markdown-editor-lexical";
import {
  isNotesNotEnabled,
  useAdvisorNote,
  useSaveAdvisorNote,
} from "@/lib/hooks/use-flow-instances/use-flow-instances";
import { useT } from "@/lib/i18n/use-t";
import { NOT_ENABLED, T } from "./advisor-notes-editor.i18n";

type AdvisorNotesEditorProps = {
  flowInstanceId: string;
  flowPageId: string;
  goalId?: string | null;
  isEditable?: boolean;
  /** QL-43 — the page marks a note mandatory: show the asterisk. */
  isRequired?: boolean;
  /** QL-43 — the note is required, still empty, and the wizard is showing gaps. */
  hasValidationError?: boolean;
  context?: {
    flowName: string;
    clientName: string;
    pageName: string;
    riskScore?: number;
    language?: string;
  };
};

export function AdvisorNotesEditor({
  flowInstanceId,
  flowPageId,
  goalId,
  isEditable = true,
  isRequired = false,
  hasValidationError = false,
  context,
}: AdvisorNotesEditorProps) {
  const t = useT(T);
  const notEnabledText = useT(NOT_ENABLED).message;

  // Subscribe to the React Query cache so external updates (e.g. the AI chat
  // saving a note, which invalidates ["flowInstances", id]) refetch and refresh
  // the editor.
  const { notes: serverNotes, isLoading } = useAdvisorNote(flowInstanceId, flowPageId, goalId);
  const saveMutation = useSaveAdvisorNote(flowInstanceId);
  const { mutate: saveNote } = saveMutation;

  const [notes, setNotes] = useState("");
  // Tracks the value we believe is currently persisted on the server. Used both
  // to detect external changes (sync effect) and to avoid re-saving a value that
  // originated from the server (autosave effect).
  const serverValueRef = useRef<string | null>(null);
  // Bumping this remounts LexicalMarkdownEditor, which only seeds its content on
  // mount — required so an externally-changed value actually becomes visible.
  const [editorKey, setEditorKey] = useState(0);

  // Sync server value into local editor state on initial load and when it
  // changes externally (not for our own saves — those update serverValueRef
  // first, so the invalidation echo refetch is recognized and skipped here).
  useEffect(() => {
    if (isLoading) return;
    const next = serverNotes ?? "";
    if (next !== (serverValueRef.current ?? "")) {
      serverValueRef.current = next;
      setNotes(next);
      setEditorKey((k) => k + 1);
    }
  }, [serverNotes, isLoading]);

  // Auto-save local edits with debounce.
  useEffect(() => {
    if (isLoading || !isEditable) return;
    // Only save genuine local edits, not values just synced from the server.
    if (notes === (serverValueRef.current ?? "")) return;

    const timeoutId = setTimeout(() => {
      const saved = notes;
      saveNote(
        { pageId: flowPageId, notes: saved, goalId: goalId ?? undefined },
        {
          // Record the persisted value so the refetch triggered by the
          // mutation's invalidation does not re-seed / remount the editor.
          onSuccess: () => {
            serverValueRef.current = saved;
          },
          onError: (error) => {
            if (isNotesNotEnabled(error)) toast.error(notEnabledText);
          },
        },
      );
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [notes, flowPageId, goalId, isLoading, isEditable, saveNote, notEnabledText]);

  // The not-enabled refusal is reported by toast (see NOT_ENABLED).
  const saveFailed = saveMutation.isError && !isNotesNotEnabled(saveMutation.error);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="advisor-notes">
            {t.advisorNotes}
            {isRequired && <span className="text-destructive"> *</span>}
          </Label>
        </div>
        <div className="flex items-center justify-center py-8 border rounded-lg">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="advisor-notes">
          {t.advisorNotes}
          {isRequired && <span className="text-destructive"> *</span>}
        </Label>
        <div className="flex items-center gap-2">
          {!isEditable && <span className="text-xs text-muted-foreground">{t.readOnly}</span>}
          {isEditable && saveMutation.isPending && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t.saving}
            </span>
          )}
        </div>
      </div>
      <div className={hasValidationError ? "rounded-lg border-2 border-destructive" : undefined}>
        <LexicalMarkdownEditor
          key={editorKey}
          value={notes}
          onChange={setNotes}
          placeholder={isEditable ? t.addNotes : t.noNotes}
          showCharacterCount={isEditable}
          showSpeechToText={isEditable}
          showAI={isEditable}
          editable={isEditable}
          aiContext={context}
        />
      </div>
      {hasValidationError && <p className="text-sm text-destructive">{t.required}</p>}
      {saveFailed && (
        <p role="alert" className="text-sm text-destructive">
          {t.saveFailed}
        </p>
      )}
    </div>
  );
}
